import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import {
  productsService,
  ProductCostCalculation,
} from '../services/productsService';
import { recipesService } from '../services/recipesService';
import { Product, Recipe, ProductCategory } from 'public-contracts';

// Extended Product type for form usage (Product from API may have additional fields)
interface ProductFormData extends Partial<Product> {
  name?: string;
  description?: string;
  price?: number;
  currency?: string;
  recipeId?: string;
  baseCost?: number;
  suggestedPrice?: number;
  markupPercentage?: number;
}

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';

// Lucide React icons
import {
  Package,
  Save,
  X,
  AlertTriangle,
  DollarSign,
  Calculator,
  TrendingUp,
} from 'lucide-react';

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (product: Product) => void;
  product?: Product | null;
}

export const ModernProductForm: React.FC<ProductFormProps> = ({
  open,
  onClose,
  onSave,
  product,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [costCalculation, setCostCalculation] =
    useState<ProductCostCalculation | null>(null);
  const [calculatingCost, setCalculatingCost] = useState(false);
  const [markupPercentage, setMarkupPercentage] = useState(30);

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: 0,
    currency: 'VND',
    categoryId: undefined,
    recipeId: '',
    baseCost: undefined,
    suggestedPrice: undefined,
    markupPercentage: 30,
  });

  useEffect(() => {
    if (open) {
      loadRecipes();
      loadCategories();
      if (product) {
        const productData = product as any; // Type assertion for extended fields
        setFormData({
          name: productData.name,
          description: product.description || '',
          price: product.price || 0,
          currency: productData.currency || 'VND',
          categoryId: product.categoryId || '',
          recipeId: product.recipeId || '',
          baseCost: productData.baseCost,
          suggestedPrice: productData.suggestedPrice,
          markupPercentage: productData.markupPercentage || 30,
        });
        setMarkupPercentage(productData.markupPercentage || 30);
        if (product.recipeId && productData.baseCost) {
          // Load existing cost calculation if available
          loadCostCalculation(product.id, productData.markupPercentage || 30);
        }
      } else {
        setFormData({
          name: '',
          description: '',
          price: 0,
          currency: 'VND',
          categoryId: undefined,
          recipeId: '',
          baseCost: undefined,
          suggestedPrice: undefined,
          markupPercentage: 30,
        });
        setMarkupPercentage(30);
        setCostCalculation(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product]);

  const loadRecipes = async () => {
    try {
      const data = await recipesService.getRecipes();
      setRecipes(data || []);
    } catch (err) {
      console.error('Failed to load recipes:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await productsService.getProductCategories({
        page: 1,
        perPage: 1000, // Fetch all categories for dropdown
      });
      const categories = Array.isArray(categoriesData) ? categoriesData : [];
      console.log(
        '📋 Loaded product categories:',
        categories.length,
        categories
      );
      setCategories(categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setCategories([]);
    }
  };

  const loadCostCalculation = async (productId: string, markup?: number) => {
    if (!productId) return;

    try {
      setCalculatingCost(true);
      const calculation = await productsService.calculateProductCost(
        productId,
        markup || markupPercentage
      );
      setCostCalculation(calculation);
      setMarkupPercentage(calculation.markupPercentage);

      // Update form data with calculated values
      setFormData((prev) => ({
        ...prev,
        baseCost: calculation.baseCost,
        suggestedPrice: calculation.suggestedPrice,
        markupPercentage: calculation.markupPercentage,
      }));
    } catch (err) {
      console.error('Failed to calculate cost:', err);
      setError(err instanceof Error ? err.message : 'Failed to calculate cost');
    } finally {
      setCalculatingCost(false);
    }
  };

  const handleCalculateCost = async () => {
    if (!formData.recipeId) {
      setError(
        translate(
          'pos.products.errors.selectRecipeFirst',
          'Please select a recipe first'
        )
      );
      return;
    }

    // If editing existing product, use its ID, otherwise we can't calculate yet
    if (product?.id) {
      await loadCostCalculation(product.id, markupPercentage);
    } else {
      setError(
        translate(
          'pos.products.errors.saveProductFirst',
          'Please save the product first to calculate cost'
        )
      );
    }
  };

  const handleMarkupChange = (value: string) => {
    const markup = parseFloat(value) || 0;
    setMarkupPercentage(markup);
    if (costCalculation) {
      const newSuggestedPrice = costCalculation.baseCost * (1 + markup / 100);
      setCostCalculation({
        ...costCalculation,
        markupPercentage: markup,
        suggestedPrice: Math.round(newSuggestedPrice),
      });
      setFormData((prev) => ({
        ...prev,
        markupPercentage: markup,
        suggestedPrice: Math.round(newSuggestedPrice),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📝 Submitting product form:', formData);
    setLoading(true);
    setError(null);

    try {
      let savedProduct: Product;

      if (product) {
        // Prepare update data - include all form fields but filter out empty recipeId
        // Backend will preserve existing recipeId and inventories if not provided
        const updateData: Partial<Product> = { ...formData };

        // Remove empty recipeId - backend will preserve existing recipeId
        if (updateData.recipeId === '') {
          delete updateData.recipeId;
        }

        // categoryId is already handled correctly (undefined means no category)

        // Remove inventories if undefined - backend will preserve existing inventories
        if (updateData.inventories === undefined) {
          delete updateData.inventories;
        }

        console.log('📤 Sending update data:', updateData);
        savedProduct = await productsService.updateProduct(
          product.id,
          updateData
        );
      } else {
        // categoryId is already handled correctly (undefined means no category)
        savedProduct = await productsService.createProduct(formData);
      }

      console.log('✅ Product saved successfully:', savedProduct);
      onSave(savedProduct);
      onClose();
    } catch (err) {
      console.error('❌ Failed to save product:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save product';
      // Ensure we show the full error message
      setError(errorMessage);
      console.error('Full error details:', {
        message: errorMessage,
        error: err,
        formData,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProductFormData, value: any) => {
    console.log(`🔄 handleInputChange: ${field} =`, value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    return formData.name && formData.price !== undefined && formData.price >= 0;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>
              {product
                ? translate('pos.products.edit', 'Edit Product')
                : translate('pos.products.create', 'Create Product')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {product
              ? translate(
                  'pos.products.editDescription',
                  'Update product details'
                )
              : translate(
                  'pos.products.createDescription',
                  'Add a new product to the system'
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="break-words whitespace-normal">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {translate('pos.products.basicInfo', 'Basic Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {translate('pos.products.fields.name', 'Name')} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  placeholder={translate(
                    'pos.products.fields.namePlaceholder',
                    'Enter product name'
                  )}
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.products.fields.description', 'Description')}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={translate(
                    'pos.products.fields.descriptionPlaceholder',
                    'Enter product description'
                  )}
                  value={formData.description || ''}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoryId">
                  {translate('pos.products.fields.categoryId', 'Category')}
                </Label>
                <Select
                  value={formData.categoryId || 'none'}
                  onValueChange={(value) =>
                    handleInputChange(
                      'categoryId',
                      value === 'none' ? undefined : value
                    )
                  }
                >
                  <SelectTrigger id="categoryId" name="categoryId">
                    <SelectValue
                      placeholder={translate(
                        'pos.products.fields.categoryIdPlaceholder',
                        'Select category (optional)'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {translate('common.none', 'None')}
                    </SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {translate(category.name, category.name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">
                    {translate('pos.products.fields.price', 'Price')} *
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      placeholder={translate(
                        'pos.products.fields.pricePlaceholder',
                        'Enter price'
                      )}
                      value={formData.price || 0}
                      onChange={(e) =>
                        handleInputChange(
                          'price',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="pl-10"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">
                    {translate('pos.products.fields.currency', 'Currency')}
                  </Label>
                  <Select
                    value={formData.currency || 'VND'}
                    onValueChange={(value) =>
                      handleInputChange('currency', value)
                    }
                  >
                    <SelectTrigger id="currency" name="currency">
                      <SelectValue
                        placeholder={translate(
                          'pos.products.fields.currencyPlaceholder',
                          'Select currency'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VND">VND</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipeId">
                  {translate('pos.products.fields.recipeId', 'Recipe')}
                </Label>
                <Select
                  value={formData.recipeId || 'none'}
                  onValueChange={(value) => {
                    handleInputChange(
                      'recipeId',
                      value === 'none' ? undefined : value
                    );
                    setCostCalculation(null);
                    if (value !== 'none' && product?.id) {
                      // Auto-calculate when recipe is selected for existing product
                      setTimeout(
                        () => loadCostCalculation(product.id, markupPercentage),
                        500
                      );
                    }
                  }}
                >
                  <SelectTrigger id="recipeId" name="recipeId">
                    <SelectValue
                      placeholder={translate(
                        'pos.products.fields.recipeIdPlaceholder',
                        'Select recipe (optional)'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {translate(
                        'pos.products.recipe.noneStockBased',
                        'None (Stock-based)'
                      )}
                    </SelectItem>
                    {recipes.map((recipe) => (
                      <SelectItem key={recipe.id} value={recipe.id}>
                        {recipe.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cost Calculation Section */}
              {formData.recipeId && (
                <Card className="border-primary/20 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2 text-lg">
                      <Calculator className="h-5 w-5" />
                      <span>
                        {translate(
                          'pos.products.cost.title',
                          'Cost Calculation'
                        )}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {product?.id && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCalculateCost}
                        disabled={calculatingCost || !formData.recipeId}
                        className="w-full"
                      >
                        {calculatingCost ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                            {translate(
                              'pos.products.cost.calculating',
                              'Calculating...'
                            )}
                          </>
                        ) : (
                          <>
                            <Calculator className="h-4 w-4 mr-2" />
                            {translate(
                              'pos.products.cost.calculate',
                              'Calculate Cost'
                            )}
                          </>
                        )}
                      </Button>
                    )}

                    {costCalculation && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-sm text-muted-foreground">
                              {translate(
                                'pos.products.cost.baseCost',
                                'Base Cost'
                              )}
                            </Label>
                            <div className="text-lg font-semibold">
                              {costCalculation.baseCost.toLocaleString()}{' '}
                              {costCalculation.currency}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm text-muted-foreground">
                              {translate(
                                'pos.products.cost.suggestedPrice',
                                'Suggested Price'
                              )}
                            </Label>
                            <div className="text-lg font-semibold text-primary">
                              {costCalculation.suggestedPrice.toLocaleString()}{' '}
                              {costCalculation.currency}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="markupPercentage">
                            {translate(
                              'pos.products.fields.markupPercentage',
                              'Markup Percentage (%)'
                            )}
                          </Label>
                          <Input
                            id="markupPercentage"
                            type="number"
                            value={markupPercentage}
                            onChange={(e) => handleMarkupChange(e.target.value)}
                            min="0"
                            max="1000"
                            step="0.1"
                            className="w-full"
                          />
                          <p className="text-xs text-muted-foreground">
                            {translate(
                              'pos.products.cost.suggestedFormula',
                              'Suggested price = Base cost × (1 + {markupPercentage}%) = {suggestedPrice} {currency}',
                              {
                                markupPercentage,
                                suggestedPrice:
                                  costCalculation.suggestedPrice.toLocaleString(),
                                currency: costCalculation.currency,
                              }
                            )}
                          </p>
                        </div>

                        {costCalculation.recipeCost &&
                          costCalculation.recipeCost.breakdown.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-sm font-semibold">
                                {translate(
                                  'pos.products.cost.breakdown',
                                  'Cost Breakdown'
                                )}
                              </Label>
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {costCalculation.recipeCost.breakdown.map(
                                  (item, index) => (
                                    <div
                                      key={index}
                                      className="flex justify-between text-sm p-2 bg-background rounded"
                                    >
                                      <span>
                                        {item.ingredientName ||
                                          item.ingredientId}
                                      </span>
                                      <span className="font-medium">
                                        {item.totalCost.toLocaleString()}{' '}
                                        {costCalculation.currency}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            if (product?.id) {
                              handleInputChange(
                                'price',
                                costCalculation.suggestedPrice
                              );
                            }
                          }}
                          className="w-full"
                        >
                          <TrendingUp className="h-4 w-4 mr-2" />
                          {translate(
                            'pos.products.cost.useSuggestedPrice',
                            'Use Suggested Price'
                          )}
                        </Button>
                      </div>
                    )}

                    {formData.baseCost !== undefined && !costCalculation && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-sm text-muted-foreground">
                              {translate(
                                'pos.products.cost.baseCost',
                                'Base Cost'
                              )}
                            </Label>
                            <div className="text-lg font-semibold">
                              {formData.baseCost.toLocaleString()}{' '}
                              {formData.currency || 'VND'}
                            </div>
                          </div>
                          {formData.suggestedPrice !== undefined && (
                            <div className="space-y-1">
                              <Label className="text-sm text-muted-foreground">
                                {translate(
                                  'pos.products.cost.suggestedPrice',
                                  'Suggested Price'
                                )}
                              </Label>
                              <div className="text-lg font-semibold text-primary">
                                {formData.suggestedPrice.toLocaleString()}{' '}
                                {formData.currency || 'VND'}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={!isFormValid() || loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading
                ? translate('common.saving', 'Saving...')
                : translate('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
